import React, { useState, useEffect, useRef } from "react";
import "./Grade1ScienceLesson.css";
import { generateScienceWelcomeContent } from '../ScienceWelcomeContent';

import { awardToken } from "../../awardtoken.js";
import defaultProfile from "../../UserProfile/EmptyProfile.jpg";

import ProgressBar from "../ProgressBar.js";
import LessonUI from "../ScienceLessonUI.js";
import { generateScienceQuizQuestions, generateScienceFinalQuestions } from "../ScienceQuizPools.js";
import { ScienceStoryModeQuiz, ScienceFinalQuizUI } from "../ScienceStoryModeStructure.js";
import { ScienceLessonPages } from "../ScienceLessonPages.js";

// ---- SET GRADE AT THE TOP! ----
const GRADE = 1;

// ----- Construct lesson steps (welcome, all slides, quizzes, final quiz/result) -----
const lessonSteps = [
  // Optionally, add a Welcome slide first (so every grade has an intro)
  {
    title: `Welcome to Grade ${GRADE} Science`,
    content: generateScienceWelcomeContent(GRADE, ScienceLessonPages),
    type: "slide",
    chapter: 0
  },
  // --- All chapter slides/quizzes dynamically ---
  ...((ScienceLessonPages[GRADE][1] || []).map(s => ({ ...s, type: "slide", chapter: 1 }))),
  { type: "chapterQuiz", chapter: 1 },
  ...((ScienceLessonPages[GRADE][2] || []).map(s => ({ ...s, type: "slide", chapter: 2 }))),
  { type: "chapterQuiz", chapter: 2 },
  // Add more chapters as needed for higher grades
  { type: "finalQuiz" },
  { type: "finalResult" }
];

export default function Grade1ScienceLesson({ onAwardTokens }) {
  const [currentStep, setCurrentStep] = useState(0);
  const step = lessonSteps[currentStep];

  // Final quiz states (reset each attempt)
  const [finalQuestions, setFinalQuestions] = useState([]);
  const [finalIndex, setFinalIndex] = useState(0);
  const [finalScore, setFinalScore] = useState(0);
  const [finalFinished, setFinalFinished] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300);

  // LocalStorage/token/user as before
  const [gradeDone, setGradeDone] = useState(false);
  const hasAwardedTokens = useRef(false);
  const [user, setUser] = useState(null);

  // ---- Robust LocalStorage completion & user state
  useEffect(() => {
    const logged = JSON.parse(localStorage.getItem("loggedInUser"));
    if (logged) setUser(logged);
    const doneScience = localStorage.getItem(`grade${GRADE}ScienceComplete`);
    if (doneScience === "true") {
      setGradeDone(true);
    }
  }, []);

  useEffect(() => {
    if (gradeDone) {
      localStorage.setItem(`grade${GRADE}ScienceComplete`, "true");
    }
  }, [gradeDone]);

  // Timer for final quiz
  useEffect(() => {
    if (step.type === "finalQuiz" && !finalFinished && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFinalFinished(true);
    }
  }, [step.type, finalFinished, timeLeft]);

  // Award tokens if final quiz ≥ 80% (call parent, update local user)
  useEffect(() => {
  if (finalFinished && !hasAwardedTokens.current) {
    const percent = (finalScore / finalQuestions.length) * 100;
    if (percent >= 80) {
      awardToken();
      const updated = JSON.parse(localStorage.getItem("loggedInUser"));
      setUser(updated);
      if (typeof onAwardTokens === "function") {
        onAwardTokens(1);
      }
    }

    // --- Save final quiz result for dashboard ---
    const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
    quizResults["science_g1"] = {
      ...(quizResults["science_g1"] || {}),
      finalQuizScore: finalScore,
      finalQuizTotal: finalQuestions.length,
      lastCompleted: Date.now(),
    };
    localStorage.setItem("quizResults", JSON.stringify(quizResults));
    // -------------------------------------------

    setGradeDone(true);
    hasAwardedTokens.current = true;
  }
}, [finalFinished, finalScore, finalQuestions.length, onAwardTokens]);


  // ---- Final quiz logic with reset on new attempt (including double-retake edge case)
  useEffect(() => {
    if (step.type === "finalQuiz") {
      setFinalQuestions(generateScienceFinalQuestions(GRADE, 10));
      setFinalIndex(0);
      setFinalScore(0);
      setFinalFinished(false);
      setTimeLeft(300);
      hasAwardedTokens.current = false;
    }
    // eslint-disable-next-line
  }, [step.type]);

  // Defensive: If step is undefined, show a fallback (should never hit unless there's a bug)
  if (!step) return <div>Unknown error! No more steps. Please restart the lesson.</div>;

  // Main final quiz answer handler
  function handleFinalQuizAnswer(opt) {
    const q = finalQuestions[finalIndex];
    const correct = opt === q.correctAnswer ? 1 : 0;
    const nextScore = finalScore + correct;
    if (finalIndex < finalQuestions.length - 1) {
      setFinalScore(prev => prev + correct);
      setFinalIndex(prev => prev + 1);
    } else {
      setFinalScore(nextScore);
      setFinalFinished(true);
    }
  }

  // ---- RENDER ----
  return (
    <div className="grade1-math-page">
      <div className="grade1-math-page-scale">
        {/* Progress bar */}
        {step.type !== "finalQuiz" && (
          <ProgressBar progress={Math.round((currentStep / (lessonSteps.length - 1)) * 100)} />
        )}

        {/* Slides */}
        {step.type === "slide" && (
          <LessonUI
            pages={[step]}
            currentPageIndex={0}
            onPrevPage={currentStep > 0 ? () => setCurrentStep(cs => cs - 1) : undefined}
            onNextPage={
              lessonSteps[currentStep + 1]?.type === "slide"
                ? () => setCurrentStep(cs => cs + 1)
                : undefined
            }
            onStartQuiz={
              lessonSteps[currentStep + 1]?.type === "chapterQuiz"
                ? () => setCurrentStep(cs => cs + 1)
                : undefined
            }
            backTo="/science"
          />
        )}

        {/* Chapter Quick Quiz */}
        {step.type === "chapterQuiz" && (() => {
          const questions = generateScienceQuizQuestions(GRADE, step.chapter);
          // Log for debugging
          console.log("step.chapter", step.chapter, "questions", questions);
          // DEFENSIVE CHECK!
          if (
            !questions ||
            !Array.isArray(questions) ||
            questions.length === 0 ||
            typeof questions[0]?.question !== "string"
          ) {
            return (
              <div style={{ color: "red", textAlign: "center", margin: "32px 0" }}>
                <b>Sorry, no quiz questions found for this chapter!</b>
              </div>
            );
          }
          return (
            <ScienceStoryModeQuiz
              questions={questions}
              onFinish={() => setCurrentStep(cs => cs + 1)}
              studentAvatar={user?.avatar ?? defaultProfile}
            />
          );
        })()}

        {/* Final Quiz */}
        {step.type === "finalQuiz" && (
          <ScienceFinalQuizUI
            questions={finalQuestions}
            currentIndex={finalIndex}
            score={finalScore}
            timeLeft={timeLeft}
            onAnswer={handleFinalQuizAnswer}
            quizFinished={finalFinished}
            onReturn={() => setCurrentStep(cs => cs + 1)}
          />
        )}

        {/* Final Quiz Result (results/congrats/token) */}
        {step.type === "finalResult" && (
          <div className="final-quiz-container" style={{ textAlign: "center" }}>
            <h2 style={{ color: "#d4145a" }}>Final Quiz Completed!</h2>
            <p style={{ fontSize: 22, margin: "18px 0" }}>
              Your Score: {finalScore}/{finalQuestions.length} (
              {Math.round((finalScore / finalQuestions.length) * 100)}%)
            </p>
            {Math.round((finalScore / finalQuestions.length) * 100) >= 80 ? (
              <div style={{ color: "#228B22", fontSize: 24, margin: "16px 0" }}>
                🎉 Congratulations! You earned a Science token!
              </div>
            ) : (
              <div style={{ color: "#e67e22", fontSize: 20, margin: "16px 0" }}>
                Try again to earn a Science token (80% required).
              </div>
            )}
            <button
              className="final-quiz-finish-btn"
              style={{ marginTop: 28 }}
              onClick={() => setCurrentStep(0)}
            >
              Return to Lesson
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
